/**
 * FontAwesome Free Icons Data
 * License: Icons - CC BY 4.0, Fonts - SIL OFL 1.1, Code - MIT
 * Attribution: Icons by Font Awesome (https://fontawesome.com)
 * 
 * This file dynamically loads ALL available FontAwesome Free icons
 * and organizes them into searchable categories.
 */

import * as SolidIcons from '@fortawesome/free-solid-svg-icons';
import * as RegularIcons from '@fortawesome/free-regular-svg-icons';

// Combine all icons
const ALL_ICONS = { ...SolidIcons, ...RegularIcons };

// Manual category mappings for common icons (these get priority placement)
const CATEGORY_MAPPINGS = {
  'Common': [
    'faHouse', 'faUser', 'faUsers', 'faGear', 'faCog', 'faMagnifyingGlass', 'faSearch',
    'faPlus', 'faMinus', 'faCheck', 'faXmark', 'faTimes', 'faStar', 'faHeart',
    'faBell', 'faBookmark', 'faFlag', 'faTag', 'faTags', 'faThumbsUp', 'faThumbsDown',
    'faComment', 'faComments', 'faEnvelope', 'faPaperPlane', 'faShare', 'faShareNodes',
    'faLink', 'faCopy', 'faClipboard', 'faTrash', 'faTrashCan', 'faPen', 'faPencil',
    'faDownload', 'faUpload', 'faCloud', 'faCloudArrowUp', 'faCloudArrowDown',
    'faCircle', 'faSquare', 'faPlay', 'faPause', 'faStop',
  ],
  'Arrows & Navigation': [
    'faArrowUp', 'faArrowDown', 'faArrowLeft', 'faArrowRight',
    'faArrowsRotate', 'faRotate', 'faRotateLeft', 'faRotateRight',
    'faChevronUp', 'faChevronDown', 'faChevronLeft', 'faChevronRight',
    'faAnglesUp', 'faAnglesDown', 'faAnglesLeft', 'faAnglesRight',
    'faCaretUp', 'faCaretDown', 'faCaretLeft', 'faCaretRight',
    'faCircleArrowUp', 'faCircleArrowDown', 'faCircleArrowLeft', 'faCircleArrowRight',
    'faArrowUpRightFromSquare', 'faExternalLinkAlt', 'faArrowRightFromBracket',
    'faArrowRightToBracket', 'faSignIn', 'faSignOut', 'faExpand', 'faCompress',
    'faUpDownLeftRight', 'faArrowsAlt', 'faMaximize', 'faMinimize',
  ],
  'Media & Player': [
    'faPlay', 'faPause', 'faStop', 'faForward', 'faBackward', 'faForwardStep', 'faBackwardStep',
    'faForwardFast', 'faBackwardFast', 'faShuffle', 'faRepeat', 'faVolumeHigh', 'faVolumeLow',
    'faVolumeOff', 'faVolumeXmark', 'faVolumeMute', 'faMusic', 'faHeadphones', 'faHeadphonesSimple',
    'faMicrophone', 'faMicrophoneSlash', 'faMicrophoneLines', 'faVideo', 'faVideoSlash',
    'faCamera', 'faCameraRetro', 'faImage', 'faImages', 'faFilm', 'faPhotoFilm',
    'faClapperboard', 'faPodcast', 'faRadio', 'faTv', 'faDisplay', 'faDesktop',
    'faRecordVinyl', 'faCompactDisc', 'faCirclePlay', 'faCirclePause', 'faCircleStop',
  ],
  'Files & Folders': [
    'faFile', 'faFileLines', 'faFileAlt', 'faFilePdf', 'faFileWord', 'faFileExcel',
    'faFilePowerpoint', 'faFileImage', 'faFileVideo', 'faFileAudio', 'faFileCode',
    'faFileZipper', 'faFileArchive', 'faFileCsv', 'faFileContract', 'faFileMedical',
    'faFileInvoice', 'faFileInvoiceDollar', 'faFileSignature', 'faFileExport', 'faFileImport',
    'faFolder', 'faFolderOpen', 'faFolderPlus', 'faFolderMinus', 'faFolderTree',
    'faFolderClosed', 'faBox', 'faBoxArchive', 'faBoxOpen', 'faBoxes', 'faBoxesStacked',
    'faDatabase', 'faServer', 'faHardDrive', 'faFloppyDisk', 'faSave',
  ],
  'Communication': [
    'faPhone', 'faPhoneVolume', 'faPhoneSlash', 'faPhoneFlip', 'faMobile', 'faMobileScreen',
    'faMobileScreenButton', 'faTablet', 'faTabletScreen', 'faTabletScreenButton',
    'faLaptop', 'faLaptopCode', 'faDesktop', 'faDisplay', 'faKeyboard', 'faPrint', 'faFax',
    'faAt', 'faHashtag', 'faMessage', 'faCommentDots', 'faCommentSms', 'faComments',
    'faQuoteLeft', 'faQuoteRight', 'faReply', 'faReplyAll', 'faInbox', 'faPaperclip',
    'faAddressBook', 'faAddressCard', 'faIdCard', 'faIdBadge', 'faEnvelope', 'faEnvelopeOpen',
    'faEnvelopeCircleCheck', 'faBullhorn', 'faRss', 'faPodcast', 'faSatellite', 'faSatelliteDish',
  ],
  'Business & Finance': [
    'faBriefcase', 'faBriefcaseMedical', 'faBuilding', 'faBuildingColumns', 'faIndustry',
    'faLandmark', 'faStore', 'faShop', 'faStoreSlash', 'faChartLine', 'faChartBar',
    'faChartPie', 'faChartArea', 'faChartSimple', 'faChartColumn', 'faChartGantt',
    'faMoneyBill', 'faMoneyBillWave', 'faMoneyBills', 'faMoneyBill1', 'faMoneyBillTransfer',
    'faCreditCard', 'faWallet', 'faCoins', 'faSackDollar', 'faSackXmark', 'faPiggyBank',
    'faReceipt', 'faFileInvoice', 'faFileInvoiceDollar', 'faCalculator', 'faPercent',
    'faHandshake', 'faHandshakeSimple', 'faHandshakeAngle', 'faScaleBalanced', 'faScaleUnbalanced',
    'faUserTie', 'faPeopleGroup', 'faUserGroup', 'faUsersGear', 'faSitemap',
  ],
  'Status & Alerts': [
    'faCircleCheck', 'faCheckCircle', 'faCircleXmark', 'faTimesCircle', 'faCircleExclamation',
    'faExclamationCircle', 'faCircleInfo', 'faInfoCircle', 'faCircleQuestion', 'faQuestionCircle',
    'faTriangleExclamation', 'faExclamationTriangle', 'faRadiation', 'faBiohazard',
    'faShieldHalved', 'faShield', 'faShieldVirus', 'faLock', 'faLockOpen', 'faUnlock',
    'faKey', 'faKeyhole', 'faEye', 'faEyeSlash', 'faBan', 'faSlash',
    'faSpinner', 'faCircleNotch', 'faHourglass', 'faHourglassStart', 'faHourglassHalf', 'faHourglassEnd',
    'faBolt', 'faBoltLightning', 'faFire', 'faFireFlameCurved', 'faFireFlameSimple',
    'faSnowflake', 'faSun', 'faMoon', 'faCloudSun', 'faCloudMoon',
  ],
  'Objects & Things': [
    'faLightbulb', 'faKey', 'faWrench', 'faScrewdriver', 'faScrewdriverWrench', 'faHammer',
    'faToolbox', 'faMagnet', 'faPuzzlePiece', 'faCube', 'faCubes', 'faGem', 'faCrown',
    'faGift', 'faGifts', 'faCakeCandles', 'faCake', 'faGlassWater', 'faWineGlass',
    'faMartiniGlass', 'faMugHot', 'faMugSaucer', 'faCoffee', 'faUtensils', 'faSpoon',
    'faKitchenSet', 'faBowlFood', 'faBowlRice', 'faPizzaSlice', 'faBurger', 'faHotdog',
    'faAppleWhole', 'faCarrot', 'faLeaf', 'faSeedling', 'faTree', 'faTreeCity',
    'faUmbrella', 'faUmbrellaBeach', 'faGlasses', 'faSunglasses', 'faShirt', 'faSocks',
  ],
  'Travel & Transport': [
    'faCar', 'faCarSide', 'faTaxi', 'faBus', 'faBusSimple', 'faTrain', 'faTrainSubway',
    'faTrainTram', 'faPlane', 'faPlaneArrival', 'faPlaneDeparture', 'faHelicopter',
    'faRocket', 'faShip', 'faSailboat', 'faFerry', 'faBicycle', 'faMotorcycle',
    'faTruck', 'faTruckFast', 'faTruckPickup', 'faVan', 'faCaravan', 'faTractor',
    'faMapLocationDot', 'faMapLocation', 'faLocationDot', 'faLocationPin', 'faLocationCrosshairs',
    'faCompass', 'faMap', 'faMapPin', 'faRoute', 'faRoad', 'faGasPump', 'faChargingStation',
    'faParking', 'faSquareParking', 'faTrafficLight', 'faEarthAmericas', 'faEarthEurope',
    'faEarthAsia', 'faEarthAfrica', 'faGlobe', 'faMountain', 'faMountainSun', 'faWater',
    'faSuitcase', 'faSuitcaseRolling', 'faPassport', 'faTicket', 'faPlaneCircleCheck',
  ],
  'Health & Medical': [
    'faHeart', 'faHeartPulse', 'faHeartbeat', 'faStethoscope', 'faSyringe', 'faVial',
    'faVials', 'faPills', 'faCapsules', 'faTablets', 'faPrescription', 'faPrescriptionBottle',
    'faPrescriptionBottleMedical', 'faHospital', 'faHospitalUser', 'faClinicMedical',
    'faAmbulance', 'faTruckMedical', 'faUserDoctor', 'faUserNurse', 'faNurse',
    'faBandage', 'faKitMedical', 'faBriefcaseMedical', 'faXRay', 'faDna', 'faMicroscope',
    'faVirus', 'faVirusSlash', 'faVirusCovid', 'faVirusCovidSlash', 'faMaskFace', 'faHeadSideMask',
    'faLungs', 'faLungsVirus', 'faBrain', 'faBone', 'faTooth', 'faTeeth', 'faTeethOpen',
    'faWheelchair', 'faWheelchairMove', 'faCrutch', 'faPersonWalking', 'faPersonRunning',
    'faPersonSwimming', 'faPersonBiking', 'faPersonHiking', 'faHandHoldingMedical',
  ],
  'Education & Science': [
    'faBook', 'faBookOpen', 'faBookOpenReader', 'faBookBookmark', 'faBookAtlas', 'faBookBible',
    'faBookmark', 'faGraduationCap', 'faUserGraduate', 'faSchool', 'faSchoolFlag',
    'faChalkboard', 'faChalkboardUser', 'faPersonChalkboard', 'faPenRuler', 'faRuler',
    'faRulerCombined', 'faRulerHorizontal', 'faRulerVertical', 'faCompassDrafting',
    'faFlask', 'faFlaskVial', 'faAtom', 'faMagnifyingGlass', 'faMicroscope', 'faDna',
    'faLanguage', 'faSpellCheck', 'faFont', 'faItalic', 'faBold', 'faUnderline', 'faStrikethrough',
    'faSubscript', 'faSuperscript', 'faListOl', 'faListUl', 'faList', 'faListCheck',
    'faAlignLeft', 'faAlignCenter', 'faAlignRight', 'faAlignJustify', 'faIndent', 'faOutdent',
    'faParagraph', 'faTextHeight', 'faTextWidth', 'faHighlighter', 'faMarker',
  ],
  'Technology & Code': [
    'faCode', 'faCodeBranch', 'faCodeCommit', 'faCodeMerge', 'faCodePullRequest', 'faCodeCompare',
    'faCodeFork', 'faTerminal', 'faBug', 'faBugSlash', 'faRobot', 'faMicrochip', 'faMemory',
    'faWifi', 'faSignal', 'faBluetooth', 'faBluetoothB', 'faSatellite', 'faSatelliteDish',
    'faNetworkWired', 'faEthernet', 'faPlug', 'faPlugCircleCheck', 'faPlugCircleXmark',
    'faBatteryFull', 'faBatteryThreeQuarters', 'faBatteryHalf', 'faBatteryQuarter', 'faBatteryEmpty',
    'faQrcode', 'faBarcode', 'faFingerprint', 'faShieldHalved', 'faUserShield', 'faLock',
    'faServer', 'faDatabase', 'faHardDrive', 'faCloud', 'faCloudArrowUp', 'faCloudArrowDown',
    'faGears', 'faSliders', 'faToggleOn', 'faToggleOff', 'faPowerOff', 'faCircleNodes',
  ],
  'Sports & Games': [
    'faFutbol', 'faSoccerBall', 'faBasketball', 'faFootball', 'faFootballBall', 'faBaseball',
    'faBaseballBatBall', 'faVolleyball', 'faGolfBallTee', 'faTableTennisPaddleBall',
    'faBowlingBall', 'faHockeyPuck', 'faDumbbell', 'faWeightHanging', 'faPersonBiking',
    'faPersonSkiing', 'faPersonSkiingNordic', 'faPersonSnowboarding', 'faPersonSwimming',
    'faPersonRunning', 'faPersonWalking', 'faPersonHiking', 'faRankingStar', 'faMedal',
    'faTrophy', 'faAward', 'faCrown', 'faChess', 'faChessBoard', 'faChessKing', 'faChessQueen',
    'faChessRook', 'faChessBishop', 'faChessKnight', 'faChessPawn', 'faDice', 'faDiceD6',
    'faDiceD20', 'faDiceFive', 'faDiceFour', 'faDiceThree', 'faDiceTwo', 'faDiceOne',
    'faPuzzlePiece', 'faGamepad', 'faHeadset', 'faStopwatch', 'faStopwatch20', 'faFlag',
    'faFlagCheckered', 'faGun', 'faBullseye', 'faPersonRifle',
  ],
  'People & Body': [
    'faUser', 'faUserPlus', 'faUserMinus', 'faUserCheck', 'faUserXmark', 'faUserPen',
    'faUserGear', 'faUserLock', 'faUserShield', 'faUserSecret', 'faUserNinja', 'faUserAstronaut',
    'faUsers', 'faUserGroup', 'faPeopleGroup', 'faPeopleLine', 'faPeopleRoof', 'faPeopleArrows',
    'faPersonDress', 'faChild', 'faChildDress', 'faChildren', 'faBaby', 'faBabyCarriage',
    'faHand', 'faHandPointer', 'faHandPointUp', 'faHandPointDown', 'faHandPointLeft', 'faHandPointRight',
    'faHandPeace', 'faHandSpock', 'faHandFist', 'faHandBackFist', 'faHandsClapping',
    'faThumbsUp', 'faThumbsDown', 'faHandshake', 'faHandshakeSimple', 'faHandshakeAngle',
    'faFaceSmile', 'faFaceGrin', 'faFaceLaugh', 'faFaceMeh', 'faFaceFrown', 'faFaceSadTear',
    'faFaceAngry', 'faFaceSurprise', 'faFaceDizzy', 'faFaceGrimace', 'faSkull', 'faSkullCrossbones',
  ],
  'Weather & Nature': [
    'faSun', 'faMoon', 'faCloud', 'faCloudSun', 'faCloudMoon', 'faCloudRain', 'faCloudShowersHeavy',
    'faCloudBolt', 'faCloudSunRain', 'faCloudMoonRain', 'faSmog', 'faWind', 'faTornado',
    'faHurricane', 'faSnowflake', 'faTemperatureHigh', 'faTemperatureLow', 'faTemperatureHalf',
    'faThermometer', 'faDroplet', 'faWater', 'faFire', 'faBolt', 'faRainbow', 'faUmbrella',
    'faTree', 'faTreeCity', 'faSeedling', 'faLeaf', 'faClover', 'faPlantWilt',
    'faMountain', 'faMountainSun', 'faMountainCity', 'faVolcano', 'faEarthAmericas',
    'faEarthEurope', 'faEarthAsia', 'faEarthAfrica', 'faEarthOceania', 'faGlobe',
    'faPaw', 'faDog', 'faCat', 'faFish', 'faFishFins', 'faDove', 'faCrow', 'faKiwiBird',
    'faFeather', 'faFeatherPointed', 'faDragon', 'faHippo', 'faOtter', 'faSpider', 'faBug',
    'faLocust', 'faMosquito', 'faWorm', 'faBacteria', 'faVirus',
  ],
  'Buildings & Places': [
    'faHouse', 'faHouseChimney', 'faHouseUser', 'faHouseMedical', 'faHouseFlag', 'faHouseLaptop',
    'faBuilding', 'faBuildingColumns', 'faBuildingFlag', 'faBuildingShield', 'faBuildingUser',
    'faCity', 'faIndustry', 'faWarehouse', 'faStore', 'faStoreSlash', 'faShop', 'faShopSlash',
    'faHospital', 'faClinicMedical', 'faSchool', 'faSchoolFlag', 'faGraduationCap',
    'faChurch', 'faMosque', 'faKaaba', 'faSynagogue', 'faPlaceOfWorship', 'faTorii',
    'faMonument', 'faLandmark', 'faLandmarkDome', 'faLandmarkFlag', 'faFortAwesome',
    'faHotel', 'faBed', 'faBath', 'faRestroom', 'faToilet', 'faShower', 'faFaucet',
    'faKitchenSet', 'faCouch', 'faChair', 'faLoveseat', 'faDoorOpen', 'faDoorClosed',
  ],
  'Shapes & Symbols': [
    'faCircle', 'faCircleDot', 'faCircleHalfStroke', 'faSquare', 'faSquareFull', 'faRectangle',
    'faTriangle', 'faDiamond', 'faHeart', 'faStar', 'faStarHalfStroke', 'faStarOfLife',
    'faCertificate', 'faAsterisk', 'faHashtag', 'faAt', 'faPercent', 'faEquals',
    'faNotEqual', 'faGreaterThan', 'faLessThan', 'faGreaterThanEqual', 'faLessThanEqual',
    'faPlus', 'faMinus', 'faXmark', 'faDivide', 'faInfinity', 'faPlusMinus',
    'faQuoteLeft', 'faQuoteRight', 'faSection', 'faParagraph', 'faCopyright', 'faRegistered',
    'faTrademark', 'faCreativeCommons', 'faYinYang', 'faPeace', 'faOm', 'faDharmachakra',
    'faCross', 'faStarOfDavid', 'faStarAndCrescent', 'faHandsPraying', 'faPray',
  ],
};

// Build the icon data
const buildIconData = () => {
  const categories = {};
  const allIconsList = [];
  const usedIcons = new Set();
  
  // First, add manually categorized icons
  Object.entries(CATEGORY_MAPPINGS).forEach(([categoryName, iconNames]) => {
    const categoryIcons = [];
    
    iconNames.forEach(iconName => {
      const icon = ALL_ICONS[iconName];
      if (icon && icon.iconName) {
        const iconData = {
          name: iconName,
          displayName: icon.iconName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          icon: icon,
          keywords: [
            iconName.toLowerCase(),
            icon.iconName.toLowerCase(),
            ...icon.iconName.split('-'),
          ],
        };
        categoryIcons.push(iconData);
        allIconsList.push({ ...iconData, category: categoryName });
        usedIcons.add(iconName);
      }
    });
    
    if (categoryIcons.length > 0) {
      categories[categoryName] = categoryIcons;
    }
  });
  
  // Then, add remaining icons to an "All Other" category
  const otherIcons = [];
  Object.entries(ALL_ICONS).forEach(([iconName, icon]) => {
    if (iconName.startsWith('fa') && icon && icon.iconName && !usedIcons.has(iconName)) {
      const iconData = {
        name: iconName,
        displayName: icon.iconName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        icon: icon,
        keywords: [
          iconName.toLowerCase(),
          icon.iconName.toLowerCase(),
          ...icon.iconName.split('-'),
        ],
      };
      otherIcons.push(iconData);
      allIconsList.push({ ...iconData, category: 'More Icons' });
    }
  });
  
  // Sort "More Icons" alphabetically
  otherIcons.sort((a, b) => a.displayName.localeCompare(b.displayName));
  if (otherIcons.length > 0) {
    categories['More Icons'] = otherIcons;
  }
  
  return { categories, allIconsList };
};

const { categories: BUILT_CATEGORIES, allIconsList: ALL_ICONS_LIST } = buildIconData();

export const FA_CATEGORIES = BUILT_CATEGORIES;
export const FA_CATEGORY_NAMES = Object.keys(FA_CATEGORIES);

export const getFAIconsByCategory = (categoryName) => FA_CATEGORIES[categoryName] || [];

export const searchFAIcons = (query) => {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return [];
  
  return ALL_ICONS_LIST.filter(icon => 
    icon.keywords.some(kw => kw.includes(lowerQuery)) ||
    icon.displayName.toLowerCase().includes(lowerQuery)
  ).slice(0, 100); // Limit results for performance
};

export const getFACategoryIcon = (categoryName) => {
  const icons = FA_CATEGORIES[categoryName];
  return icons?.[0]?.icon || null;
};

// Export count for debugging
export const FA_ICON_COUNT = ALL_ICONS_LIST.length;
